// The agreement with an event's organiser, on the Event dialog (Contracts.client).
//
// Choose how the agreement is made up — an MSA with this event's schedule, a
// schedule under an MSA already in place, or a one-off agreement — upload the
// finished Word documents, sign for Shelter Stone, and the organiser is emailed a
// link to sign without an account. Which events an MSA covers is not tracked:
// choosing "under an existing MSA" is the statement that one is in place.
// Everything saves as you go, like the other sections of the Event dialog.
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, FormControlLabel, Radio, RadioGroup, Stack, TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DrawIcon from "@mui/icons-material/Draw";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ContractSummary from "./ContractSummary";
import ShelterStoneSignDialog from "./ShelterStoneSignDialog";
import { ACTIVE_CONTRACT_STATUSES, CONTRACT_STATUS, CONTRACTS_API } from "../contracts";
import { useContractConfig } from "../useContractConfig";
import { useWriteLock } from "../useWriteLock";
import { notify } from "../notify";

const BLANK = { agreement_type: "", signer_name: "", signer_email: "", message: "" };
// An organiser often signs just an event schedule — don't speak of copies then.
const copies = (contract) => (contract.documents.length > 1 ? "copies" : "copy");
const emailOk = (v) => !v.trim() || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.trim());

function DetailsForm({ form, setForm, types, lockType }) {
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  return (
    <Stack spacing={2}>
      {!lockType && (
        <RadioGroup value={form.agreement_type} onChange={set("agreement_type")}>
          {types.map(t => (
            <FormControlLabel key={t.value} value={t.value} control={<Radio size="small" />}
                              label={<Typography variant="body2">{t.label}</Typography>} />
          ))}
        </RadioGroup>
      )}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField label="Organiser's name" fullWidth value={form.signer_name} onChange={set("signer_name")}
                   helperText="The person who will sign for the organiser." />
        <TextField label="Organiser's email" fullWidth value={form.signer_email} onChange={set("signer_email")}
                   error={!emailOk(form.signer_email)}
                   helperText={emailOk(form.signer_email) ? "The signing link is sent here." : "Not a valid email address."} />
      </Stack>
      <TextField label="Message (optional)" fullWidth multiline minRows={2} value={form.message}
                 onChange={set("message")} helperText="Included in the email inviting them to sign." />
    </Stack>
  );
}

/**
 * Props: eventId (a number once the event exists), onSummary(text) — a one-line
 * description for a collapsed section. Shown only to Contracts.client holders.
 */
export default function ClientContractManager({ eventId, onSummary }) {
  const config = useContractConfig();
  const identified = typeof eventId === "number";
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [editForm, setEditForm] = useState(null);     // details of the draft/sent contract being edited
  const [signing, setSigning] = useState(null);       // contract being signed
  const [confirm, setConfirm] = useState(null);       // {kind: "void"|"delete"|"resend", contract}
  const [voidReason, setVoidReason] = useState("");
  const [uploading, setUploading] = useState(null);   // slot
  const fileInputs = useRef({});
  const { busy, run } = useWriteLock();

  const load = useCallback(() => {
    if (!identified) { setContracts([]); setLoading(true); return Promise.resolve(); }
    return axios.get(CONTRACTS_API + "/", { params: { event_id: eventId, kind: "client", include_history: true } })
      .then(res => setContracts(res.data || []))
      .finally(() => setLoading(false));
  }, [identified, eventId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (loading) return;
    const active = contracts.find(c => ACTIVE_CONTRACT_STATUSES.includes(c.status));
    const done = contracts.find(c => c.status === "completed");
    const shown = active || done;
    onSummary?.(shown
      ? [CONTRACT_STATUS[shown.status]?.label, shown.agreement_label, shown.signer_name]
          .filter(Boolean).join(" · ")
      : "Not started");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contracts, loading]);

  const types = config?.agreement_types || [];
  const current = contracts.find(c => ACTIVE_CONTRACT_STATUSES.includes(c.status));
  const previous = contracts.filter(c => c !== current);

  const replace = (updated) => setContracts(cs => cs.map(c => (c.id === updated.id ? updated : c)));

  const create = () => run(() => axios.post(CONTRACTS_API + "/client", {
    event_id: eventId, agreement_type: form.agreement_type,
    signer_name: form.signer_name.trim() || null, signer_email: form.signer_email.trim() || null,
    message: form.message.trim() || null,
  }).then(res => { setContracts(cs => [res.data, ...cs]); setCreating(false); setForm(BLANK); }));

  const saveDetails = () => run(() => axios.put(`${CONTRACTS_API}/${current.id}`, current.status === "draft" ? {
    agreement_type: editForm.agreement_type, signer_name: editForm.signer_name.trim() || null,
    signer_email: editForm.signer_email.trim() || null, message: editForm.message.trim() || null,
  } : {
    signer_name: editForm.signer_name.trim() || null, signer_email: editForm.signer_email.trim() || null,
  }).then(res => { replace(res.data); setEditForm(null); notify("Saved.", "success"); }));

  const upload = (slot, file) => {
    if (!file) return;
    const body = new FormData();
    body.append("slot", slot);
    body.append("file", file);
    setUploading(slot);
    axios.post(`${CONTRACTS_API}/${current.id}/documents`, body)
      .then(res => {
        replace(res.data);
        const doc = res.data.documents.find(d => d.slot === slot);
        notify(doc?.unknown_fields?.length
          ? `Uploaded — but it has placeholders nothing fills: ${doc.unknown_fields.join(", ")}.`
          : "Uploaded.", doc?.unknown_fields?.length ? "warning" : "success");
      })
      .catch(() => {})
      .finally(() => { setUploading(null); if (fileInputs.current[slot]) fileInputs.current[slot].value = ""; });
  };

  const removeDoc = (doc) => run(() => axios.delete(`${CONTRACTS_API}/${current.id}/documents/${doc.id}`)
    .then(res => replace(res.data)));

  const doConfirm = () => {
    const { kind, contract } = confirm;
    const req = kind === "delete" ? axios.delete(`${CONTRACTS_API}/${contract.id}`)
      : kind === "void" ? axios.post(`${CONTRACTS_API}/${contract.id}/void`, { reason: voidReason.trim() || null })
      : axios.post(`${CONTRACTS_API}/${contract.id}/resend`);
    return run(() => req.then(() => {
      notify(kind === "delete" ? "Draft deleted." : kind === "void" ? "Contract voided." : "A fresh link has been emailed.", "success");
      setConfirm(null); setVoidReason(""); load();
    }));
  };

  const action = (path, message) => run(() => axios.post(`${CONTRACTS_API}/${current.id}/${path}`)
    .then(res => { replace(res.data); notify(message, "success"); }));

  if (!identified) return null;
  if (loading) return <Stack sx={{ alignItems: "center", py: 2 }}><CircularProgress size={24} /></Stack>;

  const slots = current ? (types.find(t => t.value === current.agreement_type)?.slots || []) : [];
  const docFor = (slot) => current?.documents.find(d => d.slot === slot);
  const canSend = current?.status === "draft" && current.missing_documents.length === 0
    && current.documents.length > 0 && current.signer_email;

  return (
    <Stack spacing={1.5}>
      {config && config.converter == null && (
        <Alert severity="warning">
          Word-to-PDF conversion isn't set up on this server, so documents can't be uploaded yet.
        </Alert>
      )}

      {!current && !creating && (
        <Box>
          <Button size="small" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
            {previous.length ? "Start another organiser contract" : "Start organiser contract"}
          </Button>
        </Box>
      )}

      {creating && (
        <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5 }}>
          <Typography variant="subtitle2" gutterBottom>What does the organiser need to sign?</Typography>
          <DetailsForm form={form} setForm={setForm} types={types} />
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 1 }}>
            <Button onClick={() => { setCreating(false); setForm(BLANK); }}>Cancel</Button>
            <Button variant="contained" disabled={!form.agreement_type || !emailOk(form.signer_email) || busy}
                    onClick={create}>Continue</Button>
          </Box>
        </Box>
      )}

      {current && (
        <ContractSummary contract={current} counterpartyLabel="Organiser" defaultHistoryOpen={false}
          actions={(
            <>
              {current.status === "draft" && (
                <>
                  <Button size="small" variant="contained" startIcon={<DrawIcon />} disabled={!canSend || busy}
                          onClick={() => setSigning(current)}>Sign and send</Button>
                  <Button size="small" onClick={() => setEditForm({
                    agreement_type: current.agreement_type || "", signer_name: current.signer_name || "",
                    signer_email: current.signer_email || "", message: current.message || "" })}>Edit details</Button>
                  <Button size="small" color="error" startIcon={<DeleteIcon />}
                          onClick={() => setConfirm({ kind: "delete", contract: current })}>Delete draft</Button>
                </>
              )}
              {current.status === "sent" && (
                <>
                  <Button size="small" onClick={() => setConfirm({ kind: "resend", contract: current })}>Resend link</Button>
                  <Button size="small" onClick={() => setEditForm({
                    agreement_type: current.agreement_type, signer_name: current.signer_name || "",
                    signer_email: current.signer_email || "", message: "" })}>Change recipient</Button>
                </>
              )}
              {current.status === "signed" && (
                <Button size="small" variant="contained" disabled={busy}
                        onClick={() => action("finalise", `Signed ${copies(current)} produced and emailed.`)}>
                  Try finishing again
                </Button>
              )}
              {["sent", "signed"].includes(current.status) && (
                <Button size="small" color="error" onClick={() => setConfirm({ kind: "void", contract: current })}>
                  Void
                </Button>
              )}
            </>
          )} />
      )}

      {/* The documents this type of agreement needs, uploaded one per slot. */}
      {current?.status === "draft" && slots.map(s => {
        const doc = docFor(s.slot);
        return (
          <Box key={s.slot} sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="body2" sx={{ flex: 1, minWidth: 180 }}>
              {s.title}{s.required ? "" : " (optional)"}
              {!s.required && !doc && (
                <Typography component="span" variant="caption" color="text.secondary">
                  {" "}— leave out if it's part of the agreement document
                </Typography>
              )}
            </Typography>
            <input type="file" hidden accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                   ref={el => { fileInputs.current[s.slot] = el; }}
                   onChange={e => upload(s.slot, e.target.files?.[0])} />
            <Button size="small" variant="outlined" disabled={uploading !== null || config?.converter == null}
                    startIcon={uploading === s.slot ? <CircularProgress size={14} /> : <UploadFileIcon />}
                    onClick={() => fileInputs.current[s.slot]?.click()}>
              {uploading === s.slot ? "Converting…" : doc ? "Replace" : "Upload Word file"}
            </Button>
            {doc && (
              <Button size="small" color="error" disabled={busy} onClick={() => removeDoc(doc)}>Remove</Button>
            )}
          </Box>
        );
      })}

      {previous.length > 0 && (
        <>
          <Typography variant="caption" color="text.secondary">Earlier</Typography>
          {previous.map(c => (
            <ContractSummary key={c.id} contract={c} counterpartyLabel="Organiser"
              actions={c.status === "completed" && (
                <Button size="small" disabled={busy} onClick={() => run(() =>
                  axios.post(`${CONTRACTS_API}/${c.id}/email-copies`)
                    .then(res => { replace(res.data); notify(`Signed ${copies(c)} emailed again.`, "success"); }))}>
                  Email {copies(c)} again
                </Button>
              )} />
          ))}
        </>
      )}

      {/* Only while there are documents to upload. */}
      {(creating || current?.status === "draft") && (
      <Typography variant="caption" color="text.secondary">
        Upload the finished Word documents. Put {"{{ShelterStoneSignature}}"}, {"{{ShelterStoneName}}"} and
        {" {{ShelterStoneDate}}"} — and {"{{ClientSignature}}"}, {"{{ClientName}}"}, {"{{ClientDate}}"} — in the
        signature blocks; a signing certificate is added to the end either way.
      </Typography>
      )}

      {/* Edit details */}
      <Dialog open={editForm !== null} onClose={() => setEditForm(null)} fullWidth maxWidth="sm">
        <DialogTitle>{current?.status === "sent" ? "Change recipient" : "Organiser contract details"}</DialogTitle>
        <DialogContent dividers>
          {editForm && (
            <>
              {current?.status === "sent" && (
                <DialogContentText sx={{ mb: 2 }}>
                  Then press Resend link — the new link goes to the address saved here.
                </DialogContentText>
              )}
              <DetailsForm form={editForm} setForm={setEditForm} types={types}
                           lockType={current?.status !== "draft"} />
              {current?.status === "draft" && editForm.agreement_type !== current.agreement_type && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Documents this type doesn't use will be removed.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditForm(null)}>Cancel</Button>
          <Button variant="contained" disabled={busy || !emailOk(editForm?.signer_email || "")}
                  onClick={saveDetails}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmations */}
      <Dialog open={confirm !== null} onClose={() => setConfirm(null)} fullWidth maxWidth="xs">
        <DialogTitle>
          {confirm?.kind === "delete" ? "Delete draft?" : confirm?.kind === "void" ? "Void this contract?" : "Resend the link?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm?.kind === "delete" && "The draft and its uploaded documents will be removed."}
            {confirm?.kind === "void" && "The organiser's link stops working and nothing more can be signed. The record is kept."}
            {confirm?.kind === "resend" && `A fresh signing link is emailed to ${confirm?.contract.signer_email}. Any earlier link stops working.`}
          </DialogContentText>
          {confirm?.kind === "void" && (
            <TextField label="Reason (optional)" fullWidth sx={{ mt: 2 }} value={voidReason}
                       onChange={e => setVoidReason(e.target.value)} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button variant="contained" color={confirm?.kind === "resend" ? "primary" : "error"}
                  disabled={busy} onClick={doConfirm}>
            {confirm?.kind === "delete" ? "Delete" : confirm?.kind === "void" ? "Void" : "Resend"}
          </Button>
        </DialogActions>
      </Dialog>

      <ShelterStoneSignDialog open={signing !== null} contracts={signing ? [signing] : []}
                              recipientLabel="organiser" onClose={() => setSigning(null)}
                              onDone={() => load()} />
    </Stack>
  );
}
